// Community Feed Styles
// Reddit-inspired community feed styling

import styled from 'styled-components';
import { theme } from '../theme';
import { ChatRoomTheme } from '../../contexts/ChatRoomThemeContext';

export const CommunityFeedContainer = styled.div<{ $theme: ChatRoomTheme }>`
  background: ${props => props.$theme === 'dark' ? '#0a0a0a' : '#ffffff'};
  min-height: 100vh;
  width: 100%;
  padding: 0;
  padding-bottom: calc(80px + 60px); /* Bottom nav + filter bar */
  padding-top: 20px;
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-bottom: calc(100px + 60px);
    padding-top: 20px;
  }
`;

export const CommunityHeader = styled.header<{ $theme: ChatRoomTheme }>`
  background: ${props => props.$theme === 'dark' 
    ? 'rgba(10, 10, 10, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)'};
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem 1rem;
    margin-bottom: 0.75rem;
  }
`;

export const CommunityFeedBackButton = styled.button<{ $theme: ChatRoomTheme }>`
  background: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(0, 0, 0, 0.1)'};
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  
  &:hover {
    background: ${props => props.$theme === 'dark' 
      ? 'rgba(255, 255, 255, 0.15)' 
      : 'rgba(0, 0, 0, 0.08)'};
    border-color: ${props => props.$theme === 'dark' 
      ? 'rgba(255, 255, 255, 0.3)' 
      : 'rgba(0, 0, 0, 0.15)'};
  }
  
  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.45rem 0.875rem;
    margin-bottom: 0.75rem;
  }
`;

export const CommunityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

export const CommunityIcon = styled.div<{ $theme: ChatRoomTheme }>`
  font-size: 2.5rem;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }
`;

export const CommunityDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CommunityName = styled.h1<{ $theme: ChatRoomTheme }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  margin: 0 0 0.25rem 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const CommunityDescription = styled.p<{ $theme: ChatRoomTheme }>`
  font-size: 0.9rem;
  color: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.7)' 
    : 'rgba(0, 0, 0, 0.7)'};
  margin: 0;
  line-height: 1.4;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
  }
`;

export const CommunityStats = styled.div<{ $theme: ChatRoomTheme }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.8rem;
  color: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.6)' 
    : 'rgba(0, 0, 0, 0.6)'};
`;

export const CommunityStatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const FeedControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.25rem;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 1rem;
    margin-bottom: 0.75rem;
  }
`;

export const PostsList = styled.div`
  padding: 0 1.25rem;
  padding-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 1rem;
    padding-bottom: 1rem;
  }
`;

export const CreatePostButton = styled.button`
  position: fixed;
  bottom: calc(70px + 60px + 1rem);
  right: 1.25rem;
  background: linear-gradient(135deg, #ff8c42 0%, #ff6b9d 100%);
  color: white;
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 140, 66, 0.4);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(255, 140, 66, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    bottom: calc(70px + 60px + 0.75rem);
    right: 1rem;
    width: 52px;
    height: 52px;
    font-size: 1.25rem;
  }
`;

export const FeedEmptyState = styled.div<{ $theme: ChatRoomTheme }>`
  text-align: center;
  padding: 3rem 1.5rem;
  color: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.7)' 
    : 'rgba(0, 0, 0, 0.7)'};
`;

export const FeedEmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

export const FeedEmptyStateTitle = styled.h3<{ $theme: ChatRoomTheme }>`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  margin: 0 0 0.5rem 0;
`;

export const FeedEmptyStateText = styled.p`
  font-size: 0.9rem;
  margin: 0;
`;

export const LoadingState = styled.div<{ $theme: ChatRoomTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: ${props => props.$theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.7)' 
    : 'rgba(0, 0, 0, 0.7)'};
  font-size: 0.9rem;
`;


// Toast for whatever the profile drawer reports back (a sent request, a
// failure). Fixed rather than in flow so it does not shift the post list.
export const ChatRoomToast = styled.div<{ $theme?: ChatRoomTheme }>`
  position: fixed;
  left: 50%;
  bottom: 96px;
  transform: translateX(-50%);
  z-index: 12000;
  max-width: min(90vw, 420px);
  padding: 0.75rem 1.25rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  background: ${props => props.$theme === 'light' ? 'rgba(255, 255, 255, 0.98)' : 'rgba(20, 20, 20, 0.98)'};
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)'};
  border: 1px solid ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.18)'};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
`;
